import React from 'react';
import { createRoot } from 'react-dom/client';
import { ExploreConfig, MountOptions } from '@explore-with-ai/core';
import ExploreWithAI from '../components/ExploreWithAI';

/**
 * Mount the ExploreWithAI widget to a DOM element
 */
export function mount(selector: string | HTMLElement, options: MountOptions): void {
  const container = typeof selector === 'string' 
    ? document.querySelector(selector) 
    : selector;

  if (!container) {
    throw new Error(`Could not find container element: ${selector}`);
  }

  const root = createRoot(container);
  
  root.render(
    React.createElement(ExploreWithAI, {
      config: options.config,
      onAnalytics: options.onAnalytics,
      className: `explore-with-ai--${options.theme || 'light'} explore-with-ai--${options.size || 'medium'}`
    })
  );
}

/**
 * Auto-mount widgets based on data attributes
 */
export function autoMount(): void {
  const widgets = document.querySelectorAll('[data-explore-with-ai]');
  
  widgets.forEach(element => {
    const configData = element.getAttribute('data-explore-with-ai');
    
    if (!configData) return;
    
    try {
      const config: ExploreConfig = JSON.parse(configData);
      const theme = element.getAttribute('data-theme') as 'light' | 'dark' || 'light';
      const size = element.getAttribute('data-size') as 'small' | 'medium' | 'large' || 'medium';
      
      mount(element as HTMLElement, {
        config,
        theme,
        size,
        onAnalytics: (event: any) => {
          // Emit custom event for analytics
          const customEvent = new CustomEvent('explore-with-ai:analytics', {
            detail: event,
            bubbles: true
          });
          element.dispatchEvent(customEvent);
        }
      });
    } catch (error) {
      console.error('Failed to mount ExploreWithAI widget:', error);
    }
  });
}

/**
 * Initialize auto-mounting when DOM is ready
 */
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoMount);
  } else {
    autoMount();
  }
}
